import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import { CATEGORIES, CATEGORY_LABELS, SORT_OPTIONS } from '../constants';
import './Exercises.css';

export default function Exercises() {
  const [exercises,    setExercises]    = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [search,       setSearch]       = useState('');
  const [category,     setCategory]     = useState('All');
  const [sortBy,       setSortBy]       = useState('alpha');
  const [showModal,    setShowModal]    = useState(false);
  const [toast,        setToast]        = useState(null);
  const [infoModal,    setInfoModal]    = useState(null);

  const sentinelRef     = useRef(null);
  const searchTimeout   = useRef(null);
  const paramsRef       = useRef({ search: '', category: 'All', sortBy: 'alpha' });
  const pageRef         = useRef(1);
  const hasMoreRef      = useRef(false);
  const loadingMoreRef  = useRef(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', category: 'Chest', muscle_group: '', description: '', is_global: false, is_bodyweight: false,
  });
  const [saving, setSaving] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchExercises = useCallback(async ({ search: s, category: c, sortBy: so, page: p }, append = false) => {
    if (!append) setLoading(true);
    else { setLoadingMore(true); loadingMoreRef.current = true; }

    try {
      const res = await api.get('/exercises', {
        params: {
          ...(s && { search: s }),
          ...(c !== 'All' && { category: c.toLowerCase() }),
          sort: so,
          page: p,
          per_page: 100,
        },
      });
      const d = res.data;
      if (append) setExercises(prev => [...prev, ...(d.exercises || [])]);
      else setExercises(d.exercises || []);
      setTotal(d.total || 0);
      hasMoreRef.current = d.has_more || false;
      pageRef.current = p;
      paramsRef.current = { search: s, category: c, sortBy: so };
    } catch {
      if (!append) showToast('Error al cargar los ejercicios');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchExercises({ search: '', category: 'All', sortBy: 'alpha', page: 1 });
    return () => clearTimeout(searchTimeout.current);
  }, [fetchExercises]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
        const { search: s, category: c, sortBy: so } = paramsRef.current;
        fetchExercises({ search: s, category: c, sortBy: so, page: pageRef.current + 1 }, true);
      }
    }, { rootMargin: '300px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchExercises, loading]);

  function handleSearchChange(val) {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchExercises({ search: val, category: paramsRef.current.category, sortBy: paramsRef.current.sortBy, page: 1 });
    }, 300);
  }

  function handleCategoryChange(cat) {
    setCategory(cat);
    clearTimeout(searchTimeout.current);
    fetchExercises({ search: search, category: cat, sortBy: sortBy, page: 1 });
  }

  function handleSortChange(sort) {
    setSortBy(sort);
    clearTimeout(searchTimeout.current);
    fetchExercises({ search: search, category: category, sortBy: sort, page: 1 });
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('El nombre del ejercicio es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), category: form.category.toLowerCase() };
      if (user?.is_admin) {
        payload.is_global = form.is_global;
        payload.is_bodyweight = form.is_bodyweight;
      }
      await api.post('/exercises', payload);
      showToast('¡Ejercicio agregado!', 'success');
      setShowModal(false);
      setForm({ name: '', category: 'Chest', muscle_group: '', description: '', is_global: false, is_bodyweight: false });
      fetchExercises({ search: paramsRef.current.search, category: paramsRef.current.category, sortBy: paramsRef.current.sortBy, page: 1 });
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al agregar el ejercicio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <ExerciseInfoModal exercise={infoModal} onClose={() => setInfoModal(null)} />
      <div className="page">
        <div className="page-header">
          <div className="page-title">Ejercicios</div>
          <span className="text-muted text-sm">{total} ejercicios</span>
        </div>

        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            placeholder="Buscar ejercicios..."
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="filter-chips">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={'chip' + (category === cat ? ' active' : '')}
              onClick={() => handleCategoryChange(cat)}
              type="button"
            >
              {CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>

        <div className="sort-row px-16">
          <span className="text-xs text-muted">Ordenar:</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={'chip' + (sortBy === opt.value ? ' active' : '')}
                style={{ padding: '4px 10px', fontSize: '0.7rem' }}
                onClick={() => handleSortChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="skeleton-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton skeleton-tile" />
            ))}
          </div>
        ) : exercises.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💪</div>
            <div className="empty-state-title">No se encontraron ejercicios</div>
            <div className="empty-state-text">
              {search ? 'Prueba con otro término de búsqueda.' : 'Agrega tu primer ejercicio usando el botón +.'}
            </div>
          </div>
        ) : (
          <>
            <div className="exercises-grid px-16">
              {exercises.map(ex => (
                <div
                  key={ex.id}
                  className="exercise-tile"
                  onClick={() => navigate(`/exercises/${ex.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && navigate(`/exercises/${ex.id}`)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <div className="exercise-tile-top">
                    <span className={`badge badge-${(ex.category || 'other').toLowerCase()}`}>
                      {CATEGORY_LABELS[ex.category] || ex.category || 'Otro'}
                    </span>
                    {ex.is_global && (
                      <span className="badge badge-admin">Admin</span>
                    )}
                    {ex.is_bodyweight && (
                      <span className="badge badge-admin" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>Sin peso</span>
                    )}
                    {(ex.category === 'cardio' && ex.pr_weight > 0) && (
                      <span className="pr-badge">⏱ {ex.pr_weight} min</span>
                    )}
                    {(ex.category !== 'cardio' && ex.is_bodyweight && ex.pr_reps > 0) && (
                      <span className="pr-badge">🏆 {ex.pr_reps} reps</span>
                    )}
                    {(ex.category !== 'cardio' && !ex.is_bodyweight && ex.pr_weight > 0) && (
                      <span className="pr-badge">🏆 {ex.pr_weight}kg</span>
                    )}
                  </div>
                  <div className="exercise-tile-name">{ex.name}</div>
                  {ex.gif_url && (
                    <img
                      src={ex.gif_url}
                      alt={ex.name}
                      className="exercise-tile-gif"
                      loading="lazy"
                    />
                  )}
                  <div className="exercise-tile-bottom">
                    {ex.muscle_group && (
                      <span className="exercise-tile-muscle text-muted text-xs">
                        {ex.muscle_group}
                      </span>
                    )}
                    {(ex.usage_count || 0) > 0 && (
                      <span className="exercise-tile-usage text-xs text-muted">
                        {ex.usage_count} series
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="exercise-info-btn"
                    onClick={e => { e.stopPropagation(); setInfoModal(ex); }}
                    aria-label="Información del ejercicio"
                    style={{ position: 'absolute', bottom: 8, right: 8 }}
                  >
                    ℹ
                  </button>
                </div>
              ))}
            </div>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                <div className="spinner" />
              </div>
            )}
          </>
        )}
      </div>

      <button className="fab" onClick={() => setShowModal(true)} aria-label="Agregar ejercicio">+</button>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">Agregar ejercicio</div>
            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input
                  className="form-input"
                  placeholder="ej. Press de banca"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Categoría *</label>
                <select
                  className="form-input"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                >
                  {CATEGORIES.filter(c => c !== 'All').map(c => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Grupo muscular</label>
                <input
                  className="form-input"
                  placeholder="ej. Pectorales, Tríceps"
                  value={form.muscle_group}
                  onChange={e => setForm(f => ({ ...f, muscle_group: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea
                  className="form-input"
                  placeholder="Descripción opcional..."
                  rows={3}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ resize: 'none' }}
                />
              </div>
              {user?.is_admin && (
                <>
                  <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={form.is_global}
                        onChange={e => setForm(f => ({ ...f, is_global: e.target.checked }))}
                        style={{ accentColor: '#e94560', width: 18, height: 18 }}
                      />
                      Ejercicio del admin (disponible para todos)
                    </label>
                  </div>
                  <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                      <input type="checkbox" checked={form.is_bodyweight} onChange={e => setForm(f => ({ ...f, is_bodyweight: e.target.checked }))} style={{ accentColor: '#4caf50', width: 18, height: 18 }} />
                      Sin peso (solo repeticiones)
                    </label>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }}
                  onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? <span className="spinner spinner-sm" /> : 'Agregar ejercicio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
